import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import Layout from 'components/sales/layout/layout';
import { TableSection } from 'components/UI';
import Images from 'assets/images/imageData';
import localization from 'assets/localization/localization';
import ListFilters from 'components/sales/ListFilters/ListFilters';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import { MobileActionMenu, DesktopActionMenu } from 'components/layout';
import { fetchDynamicContentsList } from 'services/api/dynamicContentsApi';
import '../dynamicContentsListing.css';

const PAGE_SIZE = 15;
const INITIAL_FILTERS = {
  status: '',
  project_id: '',
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    const date = new Date(value);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return value;
  }
};

const getFilterLabel = (options, id) => {
  if (id === '' || id == null) return null;
  const match = options.find((o) => String(o.id) === String(id));
  return match?.name || id;
};

const STATUS_FILTER_OPTIONS = [
  { id: '', name: 'All' },
  { id: '1', name: 'Active' },
  { id: '0', name: 'Inactive' },
];

const formatRuleType = (rule) => {
  const map = { all: 'All', include: 'Include', exclude: 'Exclude' };
  return map[rule] || rule || '—';
};

const DynamicContentsList = () => {
  const { baseUrl } = useContext(BaseUrl);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTERS);
  const [showFilter, setShowFilter] = useState(false);
  const [listData, setListData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const pageCount = pagination?.last_page || 0;
  const from = pagination?.per_page
    ? (currentPage - 1) * pagination.per_page + 1
    : (currentPage - 1) * PAGE_SIZE + 1;

  const tableColumns = useMemo(
    () => [
      { key: 'title', label: 'Title' },
      {
        key: 'rule_type',
        label: 'Rule',
        format: (val) => formatRuleType(val),
      },
      {
        key: 'status',
        label: 'Status',
        format: (val) => (val === 1 || val === '1' ? 'Active' : 'Inactive'),
      },
      {
        key: 'created_by_name',
        label: localization.createdBy,
        format: (val) => val || '—',
      },
      {
        key: 'start_at',
        label: 'Start Date',
        format: (val) => formatDate(val),
      },
      {
        key: 'end_at',
        label: 'End Date',
        format: (val) => formatDate(val),
      },
      {
        key: '_action',
        label: 'Action',
        format: (_, row) => (
          <button
            type="button"
            className="dynamic-contents-view-details-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              history.push(`${baseUrl}/dynamic-contents/view/${row.id}`);
            }}
          >
            {localization.viewDetails}
          </button>
        ),
      },
    ],
    [baseUrl, history]
  );

  const fetchList = async (page = 1, filters = filterData) => {
    setIsLoading(true);
    try {
      const apiFilters = {
        pageSize: PAGE_SIZE,
        content_type: 'BANNER',
        target_type: 'project',
        ...filters,
      };
      const { contents, pagination: pageMeta } = await fetchDynamicContentsList(page, apiFilters);
      setListData(contents);
      setPagination(pageMeta);
      setCurrentPage(page);
    } catch {
      setListData([]);
      setPagination({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1, INITIAL_FILTERS);
  }, []);

  const onPageChange = async (val) => {
    const nextPage = (val?.selected ?? 0) + 1;
    await fetchList(nextPage, filterData);
  };

  const filterToggle = () => setShowFilter(!showFilter);

  const applyFilter = async (filterPayload) => {
    setFilterData(filterPayload);
    setIsLoading(true);
    try {
      await fetchList(1, filterPayload);
    } finally {
      setIsLoading(false);
    }
    filterToggle();
  };

  const removeFilter = () => {
    setFilterData(INITIAL_FILTERS);
    fetchList(1, INITIAL_FILTERS);
  };

  const hasActiveFilters =
    (filterData.status !== '' && filterData.status != null) ||
    Boolean(String(filterData.project_id || '').trim());

  const appliedFilterLabels = [];
  const statusLabel = getFilterLabel(STATUS_FILTER_OPTIONS, filterData.status);
  if (filterData.project_id && String(filterData.project_id).trim()) {
    appliedFilterLabels.push(`${localization.projectId}: ${filterData.project_id}`);
  }
  if (statusLabel && filterData.status !== '') {
    appliedFilterLabels.push(`Status: ${statusLabel}`);
  }

  const headerBtns = () => {
    const actionsMenuItems = [
      {
        label: localization.addDynamicContent,
        onClick: () => history.push(`${baseUrl}/dynamic-contents/add`),
        icon: Images?.ImageIcon,
      },
      {
        label: localization.showFilters,
        onClick: filterToggle,
        icon: Images?.Filter,
      },
    ];

    return (
      <div className="d-flex align-items-center flex-wrap gap-2 dynamic-contents-header-actions">
        <MobileActionMenu items={actionsMenuItems} toggleLabel="Actions" />
        <DesktopActionMenu items={actionsMenuItems} toggleLabel="Actions" />
      </div>
    );
  };

  return (
    <Layout
      className="dynamic-contents-listing-page"
      title={localization.dynamicContents}
      Buttons={headerBtns}
      isGoback
      isLoading={isLoading}
      linkFlag
      linkUrl={`${baseUrl}/dashboard`}
      useNewHeaderLayout
    >
      {hasActiveFilters && (
        <div className="dynamic-contents-applied-filters">
          <span className="dynamic-contents-applied-filters-label">Applied filters:</span>
          <span className="dynamic-contents-applied-filters-list">
            {appliedFilterLabels.join(' | ')}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary dynamic-contents-remove-filter"
            onClick={removeFilter}
          >
            Remove filter
          </button>
        </div>
      )}

      <TableSection
        className="dynamic-contents-table-section"
        title={localization.dynamicContents}
        tableData={listData}
        tableColumns={tableColumns}
        tableSectionProps={{ srNo: from, className: 'dynamic-contents-table-items' }}
        pageCount={pageCount}
        currentPage={currentPage}
        onPageChange={onPageChange}
        isLoading={isLoading}
        emptyMessage={localization.noClientDashboardContentsFound}
        hideHeader
      />

      <ListFilters
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={(val) => applyFilter(val)}
        dynamicContentsFilter
        listFilterData={{ appliedFilterValues: filterData }}
      />
    </Layout>
  );
};

export default DynamicContentsList;
